/**
 * Frontend module registry.
 *
 * Pairs backend metadata (``Process`` from ``GET /api/v1/processes``)
 * with the React components that render each module's UI, and provides
 * a single accessor (:func:`createModule`) that callers reach for
 * instead of hard-coding process names.
 *
 * Fully generic: there is no bespoke per-process UI anymore. Every
 * process renders schema-driven — ``configurator_schema`` drives the
 * generic ``DynamicConfigurator`` / ``DynamicCartLineSummary`` and
 * ``preview_type`` (``svg`` / ``mesh``) drives the generic preview.
 */

import type { ComponentType } from "react";

import type {
  FrontendModule,
  ModuleComponents,
} from "./types";

import type { Process } from "@/api/types";

import { DynamicCartLineSummary } from "@/processes/dynamic/DynamicCartLineSummary";
import { DynamicConfigurator } from "@/processes/dynamic/DynamicConfigurator";
import { DynamicPreview } from "@/processes/dynamic/DynamicPreview";

/**
 * Hand-coded component overrides per process name. Intentionally empty:
 * the bespoke built-in UIs (laser / bending / print_3d /
 * surface_finishes) were removed — every process renders through the
 * schema-driven dynamic components resolved in :func:`createModule`.
 * The slot mechanism stays so a future module *could* register
 * overrides, but adding one should be the rare exception.
 */
const LOCAL_COMPONENTS: Record<string, ModuleComponents> = {};

/**
 * Merge backend metadata with the dynamic component set for one
 * process. A process with a non-empty ``configurator_schema`` gets the
 * generic ``DynamicConfigurator`` + ``DynamicCartLineSummary``; a
 * process with a ``preview_type`` other than ``none`` gets the generic
 * ``DynamicPreview``. Components stay ``null`` when the metadata
 * doesn't enable them — callers gracefully degrade to placeholders.
 */
export function createModule(process: Process): FrontendModule {
  const components = LOCAL_COMPONENTS[process.name] ?? {};
  const dynamicSchema = hasDynamicSchema(process);
  const previewType = (process.preview_type ?? "none") as
    | "svg"
    | "mesh"
    | "none";
  const hasDynamicPreview = previewType !== "none";
  return {
    ...process,
    Configurator:
      components.Configurator ??
      (dynamicSchema
        ? (DynamicConfigurator as ComponentType<unknown> as NonNullable<ModuleComponents["Configurator"]>)
        : null),
    PreviewViewer:
      components.PreviewViewer ??
      (hasDynamicPreview
        ? (DynamicPreview as ComponentType<unknown> as NonNullable<ModuleComponents["PreviewViewer"]>)
        : null),
    AnalysisStats: components.AnalysisStats ?? null,
    ValidationPanel: components.ValidationPanel ?? null,
    CartLineSummary:
      components.CartLineSummary ??
      (dynamicSchema
        ? (DynamicCartLineSummary as NonNullable<ModuleComponents["CartLineSummary"]>)
        : null),
    cartColumnHeaders:
      components.cartColumnHeaders ??
      (dynamicSchema ? ["col.material", "col.quantity"] : []),
    defaultConfig: components.defaultConfig ?? {},
  };
}

/**
 * Look up a module by its name (process_type) or slug. Returns
 * ``null`` when nothing matches — the picker / configurator handles
 * the absence rather than throwing.
 */
export function findModule(
  processes: Process[] | undefined,
  needle: string,
): FrontendModule | null {
  if (!processes) return null;
  const match = processes.find(
    (p) => p.name === needle || p.slug === needle,
  );
  return match ? createModule(match) : null;
}

/**
 * Detect the module that handles a given filename — used by the
 * smart-routing dropzone fallback (PR 16). Returns the first module
 * whose ``accepted_file_types`` includes the file's extension; null
 * when none match.
 *
 * Lowercases the extension before comparison so ``.DXF`` and ``.dxf``
 * resolve to the same module.
 */
export function findModuleForFilename(
  processes: Process[] | undefined,
  filename: string,
): FrontendModule | null {
  if (!processes || !filename) return null;
  const ext = filename.includes(".")
    ? filename.slice(filename.lastIndexOf(".")).toLowerCase()
    : "";
  if (!ext) return null;
  const match = processes.find((p) =>
    p.accepted_file_types.some((accepted) => accepted.toLowerCase() === ext),
  );
  return match ? createModule(match) : null;
}

/**
 * True when a process carries a non-empty ``configurator_schema`` — the
 * signal that :func:`createModule` renders the generic
 * ``DynamicConfigurator`` for it. Single source of truth:
 * ``createModule`` calls this too, so the visibility gate and the
 * actual render path can never drift.
 */
export function hasDynamicSchema(process: Process): boolean {
  return (
    Boolean(process.configurator_schema) &&
    Object.keys(process.configurator_schema ?? {}).length > 0
  );
}

/**
 * True when the FE can render a working configurator for this process —
 * i.e. it declares its inputs via ``configurator_schema``. This is
 * exactly the contract of :func:`createModule` producing a non-null
 * ``Configurator``.
 *
 * The landing ``ProcessPicker`` / ``ServicesDropdown`` / Landing
 * dropzone gate on this so only configurable processes surface in the
 * picker.
 */
export function hasConfigurator(process: Process): boolean {
  return hasDynamicSchema(process);
}

/**
 * Modules sorted by ``landing_order`` — what the picker grid renders
 * top-to-bottom. Stable secondary sort by slug to break ties.
 */
export function sortByLandingOrder(processes: Process[]): Process[] {
  return [...processes].sort((a, b) => {
    if (a.landing_order !== b.landing_order) {
      return a.landing_order - b.landing_order;
    }
    return a.slug.localeCompare(b.slug);
  });
}

/**
 * Does this operation ask the customer for a FILE at all?
 *
 * Mirror of the BE's ``takes_file_input`` (``processes/file_inputs.py``)
 * — both shapes count, so a process predating the ``file_inputs`` list
 * is not mistaken for form-first: the legacy contract lives flat on the
 * process (``requires_file`` / ``accepted_file_types`` /
 * ``max_upload_mb``), the current one in the list.
 *
 * A form-first operation (KAN-736) declares none of them — its
 * configurator IS the whole input. Any affordance that would hand such
 * an operation a file has nothing to hand it to: there is no blob to
 * replace and the BE has no accepted type to validate against, so the
 * upload would silently attach a file to an operation that declares it
 * needs none (KAN-773).
 */
export function takesFileInput(source: {
  requires_file?: boolean;
  accepted_file_types?: string[];
  max_upload_mb?: number | null;
  file_inputs?: { key: string }[];
}): boolean {
  if ((source.file_inputs ?? []).length > 0) return true;
  return Boolean(
    source.requires_file ||
      (source.accepted_file_types ?? []).length > 0 ||
      source.max_upload_mb,
  );
}

/**
 * Per-file upload cap declared by a process or one of its
 * ``file_inputs`` slots, in bytes — the single source both the
 * dropzone's client-side gate and its visible "max N MB" hint read
 * from. ``undefined`` when nothing declares a cap (0/absent), which
 * leaves the dropzone on its own conservative default.
 *
 * Every process-scoped dropzone must go through this. Until KAN-713 the
 * cart dropzones passed nothing and silently inherited the 20 MB
 * default, so a tenant whose 3D module allows 50 MB advertised 50 on the
 * homepage and rejected a 30 MB .stl in the cart.
 */
export function maxUploadBytes(source: {
  max_upload_mb?: number | null;
}): number | undefined {
  return source.max_upload_mb ? source.max_upload_mb * 1024 * 1024 : undefined;
}
